import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

import {
  buildGenerateCoursePayload,
  type GenerateCourseComponentSelection,
  type GenerateCourseRequest,
} from './courseGenerationUtils';
import { COURSES_QUERY_KEY, type RawCourse } from './useCourses';

interface RawGenerateCourseResponse {
  course?: RawCourse | null;
}

export interface GenerateCourseInput {
  components: GenerateCourseComponentSelection[];
  courseName: string;
  includeSettings: boolean;
  seedCourseId: string;
}

export interface GenerateCourseResult {
  course: RawCourse | null;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function unwrapGeneratedCourse(
  response:
    | RawGenerateCourseResponse
    | LegacyKuduResponseEnvelope<RawGenerateCourseResponse>
    | null
    | undefined,
) {
  if (!response || !isRecord(response)) {
    return null;
  }

  if ('Response' in response) {
    return unwrapGeneratedCourse(
      response.Response as RawGenerateCourseResponse | null,
    );
  }

  return isRecord(response.course) ? (response.course as RawCourse) : null;
}

export function useGenerateCourse() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  return useMutation<GenerateCourseResult, ApiError, GenerateCourseInput>({
    mutationFn: async ({
      components,
      courseName,
      includeSettings,
      seedCourseId,
    }) => {
      const normalizedCourseName = courseName.trim();
      const normalizedSeedCourseId = seedCourseId.trim();

      if (!normalizedCourseName) {
        throw createDataError('Enter a course name before generating a course.');
      }

      if (!normalizedSeedCourseId) {
        throw createDataError('Choose a seed course before generating a course.');
      }

      if (!user) {
        throw createDataError('You must be signed in to generate a course.');
      }

      const instructorId = getKuduUserId(user) ?? user.uid;

      if (!instructorId) {
        throw createDataError('Unable to determine the current instructor ID.');
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      const response = await client.invokeKuduMethod<
        | RawGenerateCourseResponse
        | LegacyKuduResponseEnvelope<RawGenerateCourseResponse>,
        GenerateCourseRequest
      >(
        'GenerateCourse',
        buildGenerateCoursePayload({
          components,
          courseName: normalizedCourseName,
          includeSettings,
          instructorId,
          seedCourseId: normalizedSeedCourseId,
        }),
      );

      return {
        course: unwrapGeneratedCourse(response),
      };
    },
    onSuccess: async () => {
      // refetch, not invalidate: the course list is not mounted while the
      // instructor is on this page, and invalidating an inactive query only
      // marks it stale. The list would then mount showing the cached courses
      // and fill in the new one a moment later, which read as a slow save.
      await queryClient.refetchQueries({
        queryKey: COURSES_QUERY_KEY,
      });

      navigate('/app/courses');
    },
  });
}
